import React, { type ReactNode, useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary, Box,
  Card,
  CardContent, FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select, type SelectChangeEvent,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  CircularProgress,
  styled,
  InputAdornment
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import DeleteIcon from '@mui/icons-material/Delete'
import CakeIcon from '@mui/icons-material/Cake'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  type PersonBaseBasicInformation,
  type PersonBasicInformation,
  updatePersonBasicInfo,
  usUpdatePersonBasicInfo
} from 'services'
import {
  accordionSummaryStyles,
  cardStyles,
  detailsContainerStyles,
  skeletonStyles
} from './person-basic-information-styles'
import {
  LastUpdatedComponent,
  IconButtonWithTooltip,
  EditActions,
  PersonLogo,
  PersonLogoBinary,
  useGlobalMessageHandler,
  useLegPoliticalPartiesLuts,
  MonthSelect,
  PrefixSelect,
  SuffixSelect,
  DaySelect,
  YearInput,
  FamilyEditorDialog
} from 'components'
import {
  ethnicityOptions,
  genderOptions,
  maritalStatusOptions,
  religionOptions,
  getGenderFromPrefix
} from 'utils'
import { generateLegalName, getMonthByNumber, isImageLogo, resolveNickname } from 'helpers'
import { formatErrorFieldMessages } from 'interfaces'
import { SessionRegion, useSessionContext } from 'session'
import { CommentDialog } from 'components/common/comment-dialog'
import { internationalCommentService } from 'services/international/commentService'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useLocation } from 'react-router-dom'
import { ReactComponent as QuorumIcon } from 'icons/quorum.svg'

interface PersonBasicInfoProps {
  originalBasicInfo: PersonBaseBasicInformation | PersonBasicInformation
  loading: boolean
  updateBasicInfo: (newData: PersonBasicInformation | PersonBaseBasicInformation) => void
  allowPrefixCreation?: boolean
  onDirtyChange?: (dirty: boolean) => void
  saveTrigger?: number
}

const CenteredCircularProgress = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
})

export const PersonBasicInfoBlock: React.FC<PersonBasicInfoProps> = ({ originalBasicInfo, loading, updateBasicInfo, allowPrefixCreation = false, onDirtyChange, saveTrigger }) => {
  const [loadingClick, setLoadingClick] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [personBasicInfo, setPersonBasicInfo] = useState<PersonBaseBasicInformation | PersonBasicInformation>(originalBasicInfo)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [familyDialogOpen, setFamilyDialogOpen] = useState(false)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const { sessionData, openNewTab } = useSessionContext()
  const location = useLocation()
  const isUsModule = sessionData.region === SessionRegion.US
  const isUsEditMemberPage = location.pathname.startsWith('/us/editMember/') && isUsModule
  const isPersonBasicInformation = (obj: any): obj is PersonBasicInformation => {
    return obj != null && 'urls' in obj && 'country' in obj
  }

  let politicalPartyLutValues, loadingPoliticalPartyLutValues
  if (!isPersonBasicInformation(personBasicInfo)) {
    ({ lutValues: politicalPartyLutValues, loading: loadingPoliticalPartyLutValues } = useLegPoliticalPartiesLuts())
  }

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    setPersonBasicInfo(originalBasicInfo)
    setDisabled(true)
    onDirtyChange?.(false)
  }, [originalBasicInfo])

  useEffect(() => {
    if (saveTrigger != null && saveTrigger > 0 && !disabled) {
      void handlePostClick()
    }
  }, [saveTrigger])

  const handlePostClick = async (): Promise<void> => {
    if (originalBasicInfo.id == null) {
      return
    }
    clearMessages()
    setLoadingClick(true)

    try {
      const dataToSubmit = { ...personBasicInfo }

      if (isUsModule) {
        dataToSubmit.nickname = resolveNickname(dataToSubmit.nickname, dataToSubmit.firstName)
      }

      if (!isPersonBasicInformation(dataToSubmit)) {
        const updatedInfo = await usUpdatePersonBasicInfo(originalBasicInfo.id, dataToSubmit)
        if (updatedInfo.result == null || !updatedInfo.succeeded) {
          showErrorMessage(formatErrorFieldMessages(updatedInfo.errors))
          return
        }
        updateBasicInfo(updatedInfo.result)
      } else {
        await updatePersonBasicInfo(originalBasicInfo.id, dataToSubmit)
        updateBasicInfo(dataToSubmit)
      }
      showSuccessMessage('Person updated')
      onDirtyChange?.(false)
      setDisabled(true)
    } catch (error: any) {
      showErrorMessage(formatErrorFieldMessages(error.response?.data?.errors ?? null))
    } finally {
      setLoadingClick(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<any>): void => {
    const { name, value } = e.target

    setPersonBasicInfo((prevState: PersonBaseBasicInformation) => ({
      ...prevState,
      [name]: value
    }))
    onDirtyChange?.(true)
  }

  const handleChangeSelect = (e: SelectChangeEvent<number>, child: ReactNode): void => {
    const { name, value } = e.target

    setPersonBasicInfo((prevState: PersonBaseBasicInformation) => {
      const updatedState = {
        ...prevState,
        [name]: value
      }

      if (name === 'prefixId') {
        const genderId = getGenderFromPrefix(value as number)
        if (genderId !== null) {
          updatedState.genderId = genderId
        }
      }

      return updatedState
    })
    onDirtyChange?.(true)
  }

  const handleGenerateLegalName = (): void => {
    const updatedLegalName = generateLegalName({ ...personBasicInfo }, [])
    setPersonBasicInfo(prevPersonBasicInfo => ({
      ...prevPersonBasicInfo,
      legalName: updatedLegalName
    }))
  }

  const handleOpenFamilyDialog = (): void => {
    setFamilyDialogOpen(true)
  }

  const handleCloseFamilyDialog = (): void => {
    setFamilyDialogOpen(false)
  }

  const handleSaveFamilyDialog = (newFamilyText: string): void => {
    setPersonBasicInfo(prevState => ({
      ...prevState,
      family: newFamilyText
    }))
    setFamilyDialogOpen(false)
  }

  const generateTextFieldComponent = (xsSize: number, id: string, fieldName: string, labelValue: string, value?: string): React.ReactNode => {
    return (
            <Grid item xs={xsSize}>
                <TextField
                    id={id}
                    name={fieldName}
                    label={labelValue}
                    variant="outlined"
                    disabled={disabled}
                    value={value != null ? value : ''}
                    onChange={handleChange}
                    fullWidth
                />
            </Grid>
    )
  }

  return (
      <Card sx={cardStyles}>
        <Accordion expanded={expanded} onChange={handleAccordionChange}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content" id="panel1a-header"
                            sx={accordionSummaryStyles}>

            <div>
              <Box display={'flex'} alignItems={'center'}>
                <Typography variant="h6">Basic Information</Typography>
                {isUsEditMemberPage && (
                  <IconButtonWithTooltip
                    label={'Open person edit page'}
                    disabled={originalBasicInfo.id == null || originalBasicInfo.id === 0}
                    color="secondary"
                    onClick={() => { openNewTab(`/us/editPerson/${originalBasicInfo.id}`) }}
                    icon={<OpenInNewIcon />}
                  />
                )}
                {isPersonBasicInformation(personBasicInfo) && personBasicInfo.quorumProfileUrl != null && (
                  <IconButtonWithTooltip
                    label="See this official's page in Quorum"
                    color="secondary"
                    onClick={() => { window.open(personBasicInfo.quorumProfileUrl as string, '_blank', 'noopener') }}
                    icon={<SvgIcon component={QuorumIcon} inheritViewBox />}
                  />
                )}
              </Box>
              <LastUpdatedComponent value={personBasicInfo?.lastUpdated}></LastUpdatedComponent>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {personBasicInfo.id != null && (
                  !isPersonBasicInformation(personBasicInfo)
                    ? (
                    <PersonLogoBinary
                      personName={(personBasicInfo.firstName ?? '') + (personBasicInfo.lastName ?? '')}
                      personId={personBasicInfo.id}
                      loading={loading}
                    />
                      )
                    : (
                      <PersonLogo loading={loading}
                                  originalLogoUrl={personBasicInfo.urls?.find(url => isImageLogo(url))}
                                  personId={personBasicInfo.id}/>
                      )
                )}
                  <Box sx={detailsContainerStyles}>
                      <Stack spacing={1}>
                          {loading
                            ? (
                                  <>
                                      <Skeleton variant="text" sx={skeletonStyles}/>
                                      <Skeleton variant="text" sx={skeletonStyles}/>
                                      <Skeleton variant="text" sx={skeletonStyles}/>
                                      <Skeleton variant="text" sx={skeletonStyles}/>
                                  </>
                              )
                            : (<Grid container spacing={1}>
                                    <Grid item xs={1}>
                                        <PrefixSelect
                                            value={personBasicInfo.prefixId ?? null}
                                            onChange={handleChangeSelect}
                                            disabled={disabled}
                                            allowCreation={allowPrefixCreation}
                                        />
                                    </Grid>
                                {generateTextFieldComponent(3.5, 'first_name', 'firstName', 'First Name', personBasicInfo.firstName)}
                                {generateTextFieldComponent(3, 'middle_name', 'middleName', 'Middle Name', personBasicInfo.middleName)}
                                {generateTextFieldComponent(3.5, 'last_name', 'lastName', 'Last Name', personBasicInfo.lastName)}

                                    <Grid item xs={1}>
                                        <SuffixSelect
                                            value={personBasicInfo.suffixId ?? null}
                                            onChange={handleChangeSelect}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    {!isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(2, 'prof_suffix', 'profSuffix', 'Professional Suffix', personBasicInfo.profSuffix)}
                                    {!isPersonBasicInformation(personBasicInfo) && (
                                      <Grid item xs={3}>
                                        <TextField
                                            id={'legal_name'}
                                            name={'legalName'}
                                            label={'Legal Name'}
                                            variant="outlined"
                                            disabled={disabled}
                                            value={personBasicInfo.legalName != null ? personBasicInfo.legalName : ''}
                                            onChange={handleChange}
                                            fullWidth
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position='end'>
                                                  <IconButtonWithTooltip label={'Generate Legal Name'} color={'primary'} disabled={disabled} icon={<AutoFixHighIcon/>} onClick={handleGenerateLegalName}/>
                                                </InputAdornment>
                                              )
                                            }}
                                        />
                                      </Grid>
                                    )}
                                    {!isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(3, 'nickname', 'nickname', 'Nickname', personBasicInfo.nickname)}
                                    {!isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(2, 'pronunciation', 'pronunciation', 'Pronunciation', personBasicInfo.pronunciation)}

                                    {isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(1, 'prefix_local', 'prefixLocal', 'Prefix Local', (personBasicInfo).prefixLocal)}
                                    {isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(3.5, 'first_name_local', 'firstNameLocal', 'First Name Local', (personBasicInfo).firstNameLocal)}
                                    {isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(3, 'middle_name_local', 'middleNameLocal', 'Middle Name Local', (personBasicInfo).middleNameLocal)}
                                    {isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(3.5, 'last_name_local', 'lastNameLocal', 'Last Name Local', (personBasicInfo).lastNameLocal)}
                                    {isPersonBasicInformation(personBasicInfo) && generateTextFieldComponent(1, 'suffix_local', 'suffixLocal', 'Suffix Local', (personBasicInfo).suffixLocal)}
                                    <Grid item xs={12}>

                                <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <Typography>Additional Data</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                           <Grid container spacing={1}>
                                               <Grid item xs={2}>
                                                   <FormControl fullWidth variant="outlined">
                                                       <InputLabel id="ethnicity_id_label">Ethnicity</InputLabel>
                                                       <Select
                                                           id="ethnicity_id"
                                                           name="ethnicId"
                                                           label="Ethnicity"
                                                           labelId="ethnicity_id_label"
                                                           value={personBasicInfo.ethnicId != null ? personBasicInfo.ethnicId : ''}
                                                           onChange={handleChangeSelect}
                                                           disabled={disabled}
                                                       >
                                                           {ethnicityOptions.map((option) => (
                                                               <MenuItem key={option.id} value={option.id}>
                                                                   {option.value}
                                                               </MenuItem>
                                                           ))}
                                                       </Select>
                                                   </FormControl>
                                               </Grid>
                                               <Grid item xs={2}>
                                                   <FormControl fullWidth variant="outlined">
                                                       <InputLabel id="gender_id_label">Gender</InputLabel>
                                                       <Select
                                                           id="gender_id"
                                                           name="genderId"
                                                           label="Gender"
                                                           labelId="gender_id_label"
                                                           value={personBasicInfo.genderId ?? ''}
                                                           onChange={handleChangeSelect}
                                                           disabled={disabled}
                                                       >
                                                           {genderOptions.map((option) => (
                                                               <MenuItem key={option.id} value={option.id}>
                                                                   {option.value}
                                                               </MenuItem>
                                                           ))}
                                                       </Select>
                                                   </FormControl>
                                               </Grid>
                                               <Grid item xs={3}>
                                                   <FormControl fullWidth variant="outlined">
                                                       <InputLabel id="marital_status_id_label">Marital Status</InputLabel>
                                                       <Select
                                                           id="marital_status_id"
                                                           name="maritalStatusId"
                                                           label="Marital Status"
                                                           labelId="marital_status_id_label"
                                                           value={personBasicInfo.maritalStatusId != null ? personBasicInfo.maritalStatusId : ''}
                                                           onChange={handleChangeSelect}
                                                           disabled={disabled}
                                                       >
                                                           {maritalStatusOptions.map((option) => (
                                                               <MenuItem key={option.id} value={option.id}>
                                                                   {option.value}
                                                               </MenuItem>
                                                           ))}
                                                       </Select>
                                                   </FormControl>
                                               </Grid>
                                               {generateTextFieldComponent(5, 'spouse_name', 'spouseName', 'Spouse Name', personBasicInfo.spouseName)}
                                               {!isPersonBasicInformation(personBasicInfo) && politicalPartyLutValues != null && (
                                               <Grid item xs={3}>
                                                   <FormControl fullWidth variant="outlined">
                                                       <InputLabel id="party_id_label">Party</InputLabel>
                                                       <Select
                                                           id="party_id"
                                                           name="partyId"
                                                           label="Party"
                                                           labelId="party_id_label"
                                                           value={personBasicInfo.partyId != null ? personBasicInfo.partyId : ''}
                                                           onChange={handleChangeSelect}
                                                           disabled={sessionData.region === SessionRegion.INTL ? true : disabled || loadingPoliticalPartyLutValues}
                                                       >
                                                        {
                                                          sessionData.region === SessionRegion.US &&
                                                          politicalPartyLutValues.parties.map((option: { id: number, desc: string }) => (
                                                            <MenuItem key={`partyOption-${option.id}`} value={option.id}>
                                                              {option.desc}
                                                            </MenuItem>
                                                          ))
                                                        }
                                                       </Select>
                                                   </FormControl>
                                               </Grid>)}
                                               <Grid item xs={4}>
                                                   <FormControl fullWidth variant="outlined">
                                                       <InputLabel id="religion_id_label">Religion</InputLabel>
                                                       <Select
                                                           id="religion_id"
                                                           name="religionId"
                                                           label="Religion"
                                                           labelId="religion_id_label"
                                                           value={personBasicInfo.religionId != null ? personBasicInfo.religionId : ''}
                                                           onChange={handleChangeSelect}
                                                           disabled={disabled}
                                                       >
                                                           {religionOptions.map((option) => (
                                                               <MenuItem key={option.id} value={option.id}>
                                                                   {`${option.code} - ${option.value}`}
                                                               </MenuItem>
                                                           ))}
                                                       </Select>
                                                   </FormControl>
                                               </Grid>
                                               <Grid item xs={5}>
                                                 <TextField
                                                   id="family"
                                                   name="family"
                                                   label="Family"
                                                   variant="outlined"
                                                   disabled={disabled}
                                                   value={personBasicInfo.family != null
                                                     ? personBasicInfo.family
                                                     : ''
                                                   }
                                                   onChange={handleChange}
                                                   fullWidth
                                                   InputProps={{
                                                     endAdornment: (
                                                       <InputAdornment position='end'>
                                                         <IconButtonWithTooltip
                                                           label="Create Family Text"
                                                           color="primary"
                                                           disabled={disabled}
                                                           icon={<AutoFixHighIcon/>}
                                                           onClick={handleOpenFamilyDialog}
                                                         />
                                                       </InputAdornment>
                                                     )
                                                   }}
                                                 />
                                               </Grid>
                                               {generateTextFieldComponent(2, 'residence', 'residence', 'Residence', personBasicInfo.residence)}
                                               {generateTextFieldComponent(2, 'birth_place', 'birthplace', 'Birth Place', personBasicInfo.birthplace)}
                                               <Grid item xs={4}>
                                                 <Grid container spacing={1}>
                                                   <Grid item xs={4}>
                                                     <MonthSelect
                                                         value={getMonthByNumber(personBasicInfo.birthMonth)}
                                                         showNone={true}
                                                         onChange={(month) => {
                                                           setPersonBasicInfo((prevState: PersonBaseBasicInformation) => ({
                                                             ...prevState,
                                                             birthMonth: month?.number ?? null
                                                           }))
                                                         }}
                                                         disabled={disabled}
                                                         label="Birth Month"
                                                     />
                                                   </Grid>
                                                   <Grid item xs={4}>
                                                     <DaySelect
                                                        value={personBasicInfo.birthDate ?? undefined}
                                                        onChange={(day: number | null) => {
                                                          setPersonBasicInfo((prevState: PersonBaseBasicInformation | PersonBasicInformation): PersonBaseBasicInformation | PersonBasicInformation => {
                                                            return {
                                                              ...prevState,
                                                              birthDate: day === null ? undefined : day
                                                            }
                                                          })
                                                        }}
                                                        disabled={disabled}
                                                        label="Birth Day"
                                                        showNone={true}
                                                      />
                                                   </Grid>
                                                   <Grid item xs={4}>
                                                     <YearInput
                                                        value={personBasicInfo.birthYear}
                                                        onChange={(year) => {
                                                          setPersonBasicInfo((prevState: PersonBaseBasicInformation) => ({
                                                            ...prevState,
                                                            birthYear: year
                                                          }))
                                                        }}
                                                        disabled={disabled}
                                                        label="Birth Year"
                                                        minYear={1900}
                                                        maxYear={new Date().getFullYear()}
                                                        icon={<CakeIcon />}
                                                        onInvalidYear={showErrorMessage}
                                                      />
                                                   </Grid>
                                                 </Grid>
                                               </Grid>
                                               <Grid item xs={4}>
                                                 <Grid container spacing={1}>
                                                   <Grid item xs={4}>
                                                     <MonthSelect
                                                         value={getMonthByNumber(personBasicInfo.deathMonth)}
                                                         showNone={true}
                                                         onChange={(month) => {
                                                           setPersonBasicInfo((prevState: PersonBaseBasicInformation) => ({
                                                             ...prevState,
                                                             deathMonth: month?.number ?? null
                                                           }))
                                                         }}
                                                         disabled={disabled}
                                                         label="Death Month"
                                                     />
                                                   </Grid>
                                                   <Grid item xs={4}>
                                                     <DaySelect
                                                        value={personBasicInfo.deathDate ?? undefined}
                                                        onChange={(day: number | null) => {
                                                          setPersonBasicInfo((prevState: PersonBaseBasicInformation | PersonBasicInformation): PersonBaseBasicInformation | PersonBasicInformation => {
                                                            return {
                                                              ...prevState,
                                                              deathDate: day === null ? undefined : day
                                                            }
                                                          })
                                                        }}
                                                        disabled={disabled}
                                                        label="Death Day"
                                                        showNone={true}
                                                      />
                                                   </Grid>
                                                   <Grid item xs={4}>
                                                     <YearInput
                                                        value={personBasicInfo.deathYear}
                                                        onChange={(year) => {
                                                          setPersonBasicInfo((prevState: PersonBaseBasicInformation) => ({
                                                            ...prevState,
                                                            deathYear: year
                                                          }))
                                                        }}
                                                        disabled={disabled}
                                                        label="Death Year"
                                                        minYear={1900}
                                                        maxYear={new Date().getFullYear()}
                                                        onInvalidYear={showErrorMessage}
                                                        icon={null}
                                                      />
                                                   </Grid>
                                                 </Grid>
                                               </Grid>
                                               {isPersonBasicInformation(personBasicInfo) && (
                                                 <>
                                                   {generateTextFieldComponent(4, 'spouse_name_local', 'spouseNameLocal', 'Spouse Name Local', personBasicInfo.spouseNameLocal)}
                                                   {generateTextFieldComponent(4, 'residence_local', 'residenceLocal', 'Residence Local', personBasicInfo.residenceLocal)}
                                                   {generateTextFieldComponent(4, 'family_local', 'familyLocal', 'Family Local', personBasicInfo.familyLocal)}
                                                   {generateTextFieldComponent(2, 'prof_suffix', 'profSuffix', 'Professional Suffix', personBasicInfo.profSuffix)}
                                                   {generateTextFieldComponent(3, 'legal_name', 'legalName', 'Legal Name', personBasicInfo.legalName)}
                                                   {generateTextFieldComponent(3, 'nickname', 'nickname', 'Nickname', personBasicInfo.nickname)}
                                                   {generateTextFieldComponent(2, 'pronunciation', 'pronunciation', 'Pronunciation', personBasicInfo.pronunciation)}
                                                   <Grid item xs={2}/>
                                                   {generateTextFieldComponent(2, 'prof_suffix_local', 'profSuffixLocal', 'Professional Suffix Local', (personBasicInfo).profSuffixLocal)}
                                                   {generateTextFieldComponent(3, 'legal_name_local', 'legalNameLocal', 'Legal Name Local', (personBasicInfo).legalNameLocal)}
                                                   {generateTextFieldComponent(3, 'nickname_local', 'nicknameLocal', 'Nickname Local', (personBasicInfo).nicknameLocal)}
                                                   {generateTextFieldComponent(2, 'pronunciation_local', 'pronunciationLocal', 'Pronunciation Local', (personBasicInfo).pronunciationLocal)}
                                                 </>
                                               )}
                                           </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                                </Grid>
                              )}
                      </Stack>
                  </Box>
              </Box>
              {loadingClick && <CenteredCircularProgress><CircularProgress size={50} /></CenteredCircularProgress>}

              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                  {isPersonBasicInformation(personBasicInfo)
                    ? <CommentDialog recordId={personBasicInfo.id} recordType={'P'} commentService={internationalCommentService}/>
                    : <CommentDialog recordId={personBasicInfo.id} recordType={'P'}/>
                  }
                  <EditActions
                    entityName="Person"
                    editing={!disabled}
                    disabled={loading}
                    showCancel={isUsModule}
                    onEdit={() => { setDisabled(false) }}
                    onCancel={() => {
                      setPersonBasicInfo(originalBasicInfo)
                      clearMessages()
                      onDirtyChange?.(false)
                      setDisabled(true)
                    }}
                    onSave={() => { void handlePostClick() }}
                  />
                  <IconButtonWithTooltip label="Delete Person" color="secondary" disabled={loading || true} icon={<DeleteIcon/>}/>
              </Box>

              <FamilyEditorDialog
                open={familyDialogOpen}
                onClose={handleCloseFamilyDialog}
                onSave={handleSaveFamilyDialog}
                initialValue={personBasicInfo.family ?? ''}
              />
            </CardContent>
          </AccordionDetails>
        </Accordion>
      </Card>
  )
}
